import type React from 'react';

import { Button, Card, CardBody, CardFooter, ChangeDot, Image, Text } from '@change/ds-core-ui';
import { CloseIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';

import { FEATURED_PETITION_SLUG } from '../../shared/constants';

type Props = Readonly<{
	displayTitle: string;
	// The featured petition's thumbnail; omitted from the toast when null or still processing.
	photo: { url: string; processing: boolean } | null;
	// Exact signatures in the trailing 1-day window (the momentum stat).
	signatureCount: number;
	onDismiss: () => void;
	onCtaClick: () => void;
	// On petition-details mobile the toast must clear the sticky sign CTA at the bottom of the screen.
	signBarOffset?: boolean;
}>;

// z-4980: above the membership banner (z-4900) and page sign/app-download CTAs, below the cookie
// wall (z-4990) and modals (5000+).
export const ToastCard: React.FC<Props> = ({
	displayTitle,
	photo,
	signatureCount,
	onDismiss,
	onCtaClick,
	signBarOffset = false,
}) => {
	const { translate, localizeNumber } = useI18n();
	const showThumbnail = photo !== null && !photo.processing;

	return (
		<div
			className={cn(
				'fixed left-0 z-4980 w-full md:right-4 md:left-auto md:w-auto md:max-w-sm',
				signBarOffset ? 'bottom-24 md:bottom-4' : 'bottom-0 md:bottom-4',
			)}
			data-qa="uk-surging-petition-toast"
		>
			<Card elevation className="bg-background-base">
				<CardBody className="space-y-2">
					<div className="flex items-center gap-2">
						<ChangeDot size="small" />
						<Text size="small" className="text-typography-brand">
							<Translate value="corgi.uk_surging_petition_toast.momentum_label" />
						</Text>
					</div>
					<div className="flex gap-3">
						{showThumbnail && (
							<Image
								// decorative — the adjacent title carries the petition name
								alt=""
								src={photo.url}
								aspectRatio="16:9"
								className="w-20 shrink-0 rounded-md object-cover"
							/>
						)}
						<div className="min-w-0 space-y-1">
							<Text variant="heading" size="small" fontWeight="bold" className="text-typography-strong line-clamp-2">
								{displayTitle}
							</Text>
							<Text size="small" className="text-typography-weak">
								<TranslatePlural
									value="corgi.uk_surging_petition_toast.signed_last_day"
									count={signatureCount}
									replacements={{ countLocalized: localizeNumber(signatureCount) }}
								/>
							</Text>
						</div>
					</div>
				</CardBody>
				<CardFooter className="flex items-center justify-between gap-2">
					<Button
						as="a"
						href={`/p/${FEATURED_PETITION_SLUG}`}
						variant="secondary"
						size="small"
						onClick={onCtaClick}
						data-qa="uk-surging-petition-toast-cta"
					>
						<Translate value="corgi.uk_surging_petition_toast.cta_label" />
					</Button>
					<Button
						iconOnly
						variant="tertiary"
						size="small"
						leadingIcon={<CloseIcon size="small" />}
						aria-label={translate('corgi.uk_surging_petition_toast.dismiss_label')}
						onClick={onDismiss}
						data-qa="uk-surging-petition-toast-dismiss"
					/>
				</CardFooter>
			</Card>
		</div>
	);
};
